import { useState, useEffect } from 'react';
import { ExternalLink, X, Copy, Check } from 'lucide-react';
import {
  isInAppBrowser, isAndroid, isIOS,
  getOpenInBrowserInstruction, getExternalBrowserURL, getWebviewDiagnostics
} from '@/utils/webviewDetect';

// Re-export for Landing.js and other consumers
export { isInAppBrowser };

export function InAppBrowserBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bridging, setBridging] = useState(false);

  // Log diagnostics on mount in webviews
  useEffect(() => {
    if (!isInAppBrowser) return;
    const diag = getWebviewDiagnostics();
    console.log('[BODYMAX_WEBVIEW]', JSON.stringify(diag));
    const API = process.env.REACT_APP_BACKEND_URL;
    if (API) {
      fetch(`${API}/api/analytics/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path: window.location.pathname,
          referrer: document.referrer,
          session_id: sessionStorage.getItem('bm_analytics_sid') || 'webview_no_sid',
          source: diag.source || 'inapp',
          webview_diagnostics: diag,
        }),
      }).catch(() => {});
    }
  }, []);

  if (!isInAppBrowser || dismissed) return null;

  const instruction = getOpenInBrowserInstruction();

  /**
   * Bridge flow: request a short-lived token, build URL with it, open in browser.
   */
  const handleOpenInBrowser = async () => {
    setBridging(true);
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('bodymax-token') || sessionStorage.getItem('bodymax-token');
    let targetURL = window.location.origin;

    // If user is authenticated, create a bridge token for seamless transition
    if (token && API) {
      try {
        const res = await fetch(`${API}/api/auth/webview-bridge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          targetURL = `${window.location.origin}/dashboard?bridge_token=${data.bridge_token}`;
        }
      } catch {
        // Fallback: open without bridge token — user will need to log in again
      }
    }

    if (isAndroid) {
      // Android intent:// to open in default browser
      const intentURL = `intent://${targetURL.replace(/^https?:\/\//, '')}#Intent;scheme=https;end`;
      window.location.href = intentURL;
    } else {
      // iOS: copy the bridge URL to clipboard
      try {
        await navigator.clipboard?.writeText(targetURL);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch {
        // Fallback: prompt user
      }
    }
    setBridging(false);
  };

  const handleCopyLink = async () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('bodymax-token') || sessionStorage.getItem('bodymax-token');
    let targetURL = window.location.origin;

    // If authenticated, include bridge token in copied URL
    if (token && API) {
      try {
        const res = await fetch(`${API}/api/auth/webview-bridge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          targetURL = `${window.location.origin}/dashboard?bridge_token=${data.bridge_token}`;
        }
      } catch {}
    }

    navigator.clipboard?.writeText(targetURL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }).catch(() => {});
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[200] bg-card border-b border-accent/20 px-4 py-2.5"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
      data-testid="inapp-browser-banner"
    >
      <div className="flex items-center gap-3 max-w-lg mx-auto">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">Open in browser</p>
          <p className="text-xs text-muted-foreground mt-0.5">{instruction}</p>
        </div>

        {isAndroid ? (
          <button
            onClick={handleOpenInBrowser}
            disabled={bridging}
            className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent text-accent-foreground text-xs font-bold active:scale-95 transition-transform disabled:opacity-50"
            data-testid="open-in-browser-btn"
          >
            <ExternalLink size={14} /> {bridging ? '...' : 'Open'}
          </button>
        ) : (
          <button
            onClick={handleCopyLink}
            disabled={bridging}
            className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/10 text-foreground text-xs font-medium active:scale-95 transition-transform disabled:opacity-50"
            data-testid="copy-link-btn"
          >
            {copied ? <><Check size={14} className="text-accent" /> Copied</> : <><Copy size={14} /> Copy link</>}
          </button>
        )}

        <button
          onClick={() => setDismissed(true)}
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-white/5 active:scale-90"
          data-testid="inapp-banner-dismiss"
        >
          <X size={14} className="text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}
